import type { CSSProperties, ImgHTMLAttributes } from "react";
import type { Media } from "@zivin-labs/site-content";
import { Placeholder, type PlaceholderKind } from "./Placeholder";
import { imageUrl, buildSrcSet, IMAGE_CDN_BASE } from "../lib/imageUrl";

// Responsive width ladder for transform-URL srcSet generation.
const LADDER = [400, 800, 1200, 1600];
// Fallback `src` width when only a bare source key (no CDN base yet, or no
// srcSet support) is available.
const DEFAULT_WIDTH = 800;

interface FigureProps {
  /** Image content. When null/undefined, the Placeholder is shown instead. */
  media?: Media | null;
  /** Label for the Placeholder fallback (used until an image is populated). */
  fallbackLabel: string;
  aspect?: string;
  kind?: PlaceholderKind;
  className?: string;
  style?: CSSProperties;
  sizes?: string;
  /** LCP images: eager-load + high fetch priority. */
  priority?: boolean;
}

/** Renders an optimized image from a Media object, or the Placeholder when absent. */
export function Figure({
  media,
  fallbackLabel,
  aspect = "4/3",
  kind = "light",
  className = "",
  style = {},
  sizes,
  priority = false,
}: FigureProps) {
  if (!media || !media.url) {
    return (
      <Placeholder
        label={fallbackLabel}
        kind={kind}
        aspect={aspect}
        className={className}
        style={style}
      />
    );
  }

  // @types/react types only camelCase `fetchPriority`, but react-dom 18 renders
  // (without warning) only the lowercase `fetchpriority` DOM attribute — camelCase
  // warns and is dropped at runtime. Set lowercase + cast; revisit at React 19.
  type ImgProps = ImgHTMLAttributes<HTMLImageElement> & { fetchpriority?: "high" | "low" | "auto" };

  // A bare source key (no scheme) combined with a configured CDN base means we
  // can build a responsive transform srcSet; otherwise media.url is already a
  // ready-to-use absolute URL (Phase 2 interim + Phase 3 both provide these),
  // so it's used as-is and no srcSet is emitted.
  const isBareKey = !media.url.includes("://");
  const useTransform = isBareKey && Boolean(IMAGE_CDN_BASE);

  // Never request more pixels than the source actually has. The transform CDN
  // happily upscales, so a fixed ladder turned a 244x180 headshot into a 2.7 MB
  // 1600px PNG — slow, and blurrier than just serving the original. Cap the
  // ladder at the source's own width (or the ladder top, whichever is smaller)
  // and keep the native width as the last rung.
  const cap = media.width
    ? Math.min(media.width, LADDER[LADDER.length - 1])
    : LADDER[LADDER.length - 1];
  const ladder = [...LADDER.filter((w) => w < cap), cap];

  const src = useTransform ? imageUrl(media.url, { width: Math.min(DEFAULT_WIDTH, cap) }) : media.url;
  const srcSet = useTransform ? buildSrcSet(media.url, ladder) : undefined;

  const imgProps: ImgProps = {
    src,
    srcSet,
    alt: media.alt ?? "",
    width: media.width,
    height: media.height,
    sizes,
    decoding: "async",
    loading: priority ? "eager" : "lazy",
    fetchpriority: priority ? "high" : undefined,
  };

  const cls = ["fig", className].filter(Boolean).join(" ");
  return (
    <div className={cls} style={{ aspectRatio: aspect, ...style }}>
      <img {...imgProps} />
    </div>
  );
}
